import { RouteError } from '@/components/route-error'
import { RoutePending } from '@/components/route-pending'
import * as React from 'react'
import { requireCapability } from '@/lib/auth'
import { ImageWithSkeleton } from '@/components/ui/image-with-skeleton'
import { api,catalogApi,type AxiosError,type MaybeEnveloped,type ListEnvelope } from '@/lib/api-client'
import { createFileRoute,Outlet,useNavigate,useSearch } from '@tanstack/react-router'
import { z } from 'zod'
import { useQuery,useMutation,useQueryClient,type QueryKey } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTheme } from 'next-themes'
import { formatDistanceToNow } from 'date-fns'
import {
Pencil,
Trash2,
Tag,
X,
Plus,
MoreHorizontal,
Loader2,
Search,
Clock,
RefreshCw,
MoreVertical,
} from 'lucide-react'

import { PageBreadcrumb } from '@/components/dashboard/page-breadcrumb'
import {
Table,
TableBody,
TableCell,
TableHead,
TableHeader,
TableRow,
} from '@/components/ui/table'
import {
Card,
CardContent,
CardDescription,
CardHeader,
CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
DropdownMenu,
DropdownMenuContent,
DropdownMenuItem,
DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { ImageUpload } from '@/components/ui/image-upload'
import { DataTablePagination } from '@/components/dashboard/data-table-pagination'
import {
Tooltip,
TooltipContent,TooltipTrigger
} from '@/components/ui/tooltip'
import { queryKeys } from '@/lib/query-keys'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { extractArray } from '@/features/http'
import { DeleteConfirmationDialog } from '@/components/delete-confirmation-dialog'
import { FormDialog } from '@/components/form-dialog'
import type { Brand } from '@/types'
import { formatInStoreTz } from '@/lib/time'

const searchSchema = z.object({
  page: z.number().catch(1),
  limit: z.number().catch(10),
  search: z.string().catch(''),
})

export const Route = createFileRoute('/_dashboard/catalog/brands')({
  pendingComponent: RoutePending,
  errorComponent: RouteError,
  beforeLoad: requireCapability('MANAGE_CATALOG'),
  component: BrandsLayout,
  validateSearch: searchSchema,
})

let suppressedBrandsAutoOpenSignature: string | null = null

function BrandsLayout() {
  return <Outlet />
}

interface PlatformBrand {
  id: number
  name: string
  /** The catalog exposes its slug per platform; there is no bare `slug` field. */
  wm_slug?: string | null
  lf_slug?: string | null
  logo: string
  description: string
}

type PaginatedData<T> = {
  data: T[]
  meta: {
    page: number
    totalPages: number
    totalItems: number
  }
}

type OptimisticPaginatedContext<T> = {
  previousData: [QueryKey, PaginatedData<T> | undefined][]
}

export function BrandsPage({ selectedId }: { selectedId: string | undefined }) {
  const navigate = useNavigate()
  const { page, limit, search: searchQuery } = useSearch({ from: Route.id })
  const queryClient = useQueryClient()
  const { resolvedTheme } = useTheme()

  const [searchInput, setSearchInput] = React.useState(searchQuery)
  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false)
  const [isEditing, setIsEditing] = React.useState(false)
  const [isUploading, setIsUploading] = React.useState(false)
  const [brandToDelete, setBrandToDelete] = React.useState<Brand | null>(null)

  const [editingId, setEditingId] = React.useState<string | null>(null)

  // Form State
  const [name, setName] = React.useState('')
  const [notes, setNotes] = React.useState('')
  const [images, setImages] = React.useState<string[]>([])
  const [errors, setErrors] = React.useState<{ name?: string }>({})

  // Platform Brand Autocomplete State
  // Term whose suggestions the user selected/dismissed — searching resumes as
  // soon as the input no longer matches it (replaces the old skip-one-fetch ref).
  const [suppressedTerm, setSuppressedTerm] = React.useState<string | null>(null)
  const [suggestionsDismissed, setSuggestionsDismissed] = React.useState(false)
  const nameInputRef = React.useRef<HTMLInputElement>(null)
  const suggestionsRef = React.useRef<HTMLDivElement>(null)

  const [selectedPlatformBrand, setSelectedPlatformBrand] = React.useState<{
    id: number
    slug: string
  } | null>(null)

  const [shouldFocusDescription, setShouldFocusDescription] = React.useState(false)
  const descriptionRef = React.useRef<HTMLTextAreaElement>(null)
  const pageSignature = `${page}:${limit}:${searchQuery}`

  const setSelectedId = React.useCallback(
    (id: string | null) => {
      if (id) {
        navigate({
          to: '/catalog/brands/$item',
          params: { item: id },
          search: (prev) => ({ ...prev }),
        })
        return
      }

        navigate({ to: '/catalog/brands', search: (prev) => ({ ...prev }) })
    },
    [navigate],
  )

  // Debounced search → sync to URL
  React.useEffect(() => {
    const timer = setTimeout(() => {
      // Only a changed term resets to page 1 — on mount the term is unchanged,
      // so reloads and shared links keep their ?page=.
      navigate({ to: '.', search: (prev) => ((prev.search ?? '') === searchInput ? prev : { ...prev, search: searchInput, page: 1 }), replace: true })
    }, 500)
    return () => clearTimeout(timer)
  }, [searchInput, navigate])

  // ── TanStack Query: brands list ──
  const brandsQuery = useQuery({
    queryKey: [...queryKeys.brands(''), { page, limit, search: searchQuery }],
    queryFn: async () => {
      const response = await api.get<ListEnvelope>(`/brands`, {
        params: { page, limit, search: searchQuery || undefined },
      })
      const data = extractArray<Brand>(response)
      // The API returns `meta.total`, not `totalItems` — normalize it so the
      // "of N" count and the optimistic add/delete updates read the real total.
      const meta = {
        page: response.meta?.page ?? page,
        totalPages: response.meta?.totalPages ?? 1,
        totalItems: response.meta?.total ?? response.meta?.totalItems ?? data.length,
      }
      return { data, meta }
    },
  })

  // ── TanStack Query: selected brand direct-fetch (for shareable links) ──
  const selectedBrandQuery = useQuery({
    queryKey: queryKeys.brand(selectedId ?? ''),
    queryFn: async () => {
      const response = await api.get<MaybeEnveloped<Brand>>(`/brands/${selectedId}`)
      return (response?.data || response) as Brand
    },
    enabled: !!selectedId,
  })

  const brands = brandsQuery.data?.data || []
  const totalItems = brandsQuery.data?.meta?.totalItems || brands.length
  const totalPages = brandsQuery.data?.meta?.totalPages || 1
  const loading = brandsQuery.isLoading

  const currentPageHasSelectedBrand = !!selectedId && brands.some((brand) => brand.slug === selectedId || brand.id === selectedId)
  const effectiveSelectedId = (() => {
    if (currentPageHasSelectedBrand) return selectedId
    if (loading || brands.length === 0) return undefined
    if (!selectedId && suppressedBrandsAutoOpenSignature === pageSignature) return undefined
    return brands[0].slug || brands[0].id
  })()
  const selectedBrand =
    brands.find((b) => b.slug === effectiveSelectedId || b.id === effectiveSelectedId) ||
    selectedBrandQuery.data ||
    null

  const closeSelectedBrand = React.useCallback(() => {
    suppressedBrandsAutoOpenSignature = pageSignature
    setSelectedId(null)
  }, [pageSignature, setSelectedId])

  React.useEffect(() => {
    if (!effectiveSelectedId || effectiveSelectedId === selectedId) return
    setSelectedId(effectiveSelectedId)
  }, [effectiveSelectedId, selectedId, setSelectedId])

  React.useEffect(() => {
    return () => {
      window.setTimeout(() => {
        if (!window.location.pathname.startsWith('/catalog/brands')) {
          suppressedBrandsAutoOpenSignature = null
        }
      }, 0)
    }
  }, [])

  // ── TanStack Query: save mutation ──
  const saveBrand = useMutation<void, unknown, { name: string; details: string; images: string[], id?: string, catalogSlug?: string, catalogBrandId?: number }, OptimisticPaginatedContext<Brand>>({
    mutationFn: async (payload) => {
      if (payload.id) {
        const { id, ...data } = payload
        await api.patch(`/brands/${id}`, data)
      } else {
        await api.post(`/brands`, payload)
      }
    },
    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.brands('') })
      const previousData = queryClient.getQueriesData<PaginatedData<Brand>>({ queryKey: queryKeys.brands('') })

      queryClient.setQueriesData<PaginatedData<Brand>>(
        { queryKey: queryKeys.brands('') },
        (old) => {
          if (!old) return old

          if (payload.id) {
            return {
              ...old,
              data: old.data.map((item) =>
                item.id === payload.id ? { ...item, ...payload } : item
              ),
            }
          }

          const tempBrand: Brand = {
            id: `temp-${Date.now()}`,
            name: payload.name,
            slug: payload.name,
            details: payload.details,
            images: payload.images,
          }

          return {
            ...old,
            data: old.meta.page === 1 ? [tempBrand, ...old.data] : old.data,
            meta: { ...old.meta, totalItems: old.meta.totalItems + 1 },
          }
        }
      )

      return { previousData }
    },
    onSuccess: (_, payload) => {
      toast.success(`Brand ${payload.id ? 'updated' : 'created'} successfully`)
      handleCloseDialog(false)
    },
    onError: (error: unknown, _payload, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      const msg = (error as AxiosError<{ message?: string }>).response?.data?.message
      toast.error(msg || 'Failed to save brand')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.brands('') })
    },
  })

  // ── TanStack Query: delete mutation ──
  const deleteBrand = useMutation<void, unknown, string, OptimisticPaginatedContext<Brand>>({
    mutationFn: async (id: string) => {
      await api.delete(`/brands/${id}`)
    },
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: queryKeys.brands('') })
      const previousData = queryClient.getQueriesData<PaginatedData<Brand>>({ queryKey: queryKeys.brands('') })

      queryClient.setQueriesData<PaginatedData<Brand>>(
        { queryKey: queryKeys.brands('') },
        (old) => {
          if (!old) return old
          return {
            ...old,
            data: old.data.filter((item) => item.id !== id),
            meta: { ...old.meta, totalItems: Math.max(0, old.meta.totalItems - 1) },
          }
        }
      )

      return { previousData }
    },
    onSuccess: (_, deletedId) => {
      if (selectedId === deletedId) setSelectedId(null)
      toast.success('Brand deleted successfully')
    },
    onError: (_error, _id, context) => {
      if (context?.previousData) {
        for (const [key, data] of context.previousData) {
          queryClient.setQueryData(key, data)
        }
      }
      toast.error('Failed to delete brand')
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.brands('') })
    },
  })

  // Platform brand suggestions for the typed name (debounced via query key).
  const debouncedName = useDebouncedValue(name)
  const platformSearchEnabled =
    !!debouncedName.trim() && !isEditing && debouncedName !== suppressedTerm
  const platformSearchQuery = useQuery({
    queryKey: queryKeys.platformBrandSearch(debouncedName),
    queryFn: async () => {
      const response = await catalogApi.get<{ data?: PlatformBrand[] }>(
        `/brands?search=${encodeURIComponent(debouncedName)}&page=1&limit=20`,
      )
      return response?.data || []
    },
    enabled: platformSearchEnabled,
    retry: false,
    staleTime: 60_000,
  })
  const platformBrands = React.useMemo(
    () => (platformSearchEnabled ? platformSearchQuery.data ?? [] : []),
    [platformSearchEnabled, platformSearchQuery.data],
  )
  const isSearchingPlatform = platformSearchQuery.isFetching

  // Suggestions show whenever there are results and the user hasn't dismissed
  // them for the current term; a new term resets the dismissal (render-adjust).
  const [prevDebouncedName, setPrevDebouncedName] = React.useState(debouncedName)
  if (prevDebouncedName !== debouncedName) {
    setPrevDebouncedName(debouncedName)
    setSuggestionsDismissed(false)
  }
  const showSuggestions = platformBrands.length > 0 && !suggestionsDismissed

  // Close suggestions when clicking outside (acceptable useEffect exception)
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        nameInputRef.current &&
        !nameInputRef.current.contains(event.target as Node)
      ) {
        setSuggestionsDismissed(true)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleSelectPlatformBrand = (brand: PlatformBrand) => {
    setSuppressedTerm(brand.name)
    setName(brand.name)
    setNotes(brand.description || '')
    if (brand.logo) {
      setImages([brand.logo])
    }
    const catalogSlug = brand.wm_slug || brand.lf_slug
    setSelectedPlatformBrand(
      catalogSlug ? { id: brand.id, slug: catalogSlug } : null,
    )
    setSuggestionsDismissed(true)
  }

  const handleCloseDialog = (open: boolean) => {
    setIsAddDialogOpen(open)
    if (!open) {
      setTimeout(() => {
        setName('')
        setNotes('')
        setImages([])
        setErrors({})
        setIsEditing(false)
        setEditingId(null)
        setSuppressedTerm(null)
        setSuggestionsDismissed(false)
        setSelectedPlatformBrand(null)
      }, 300)
    }
  }

  const handleEdit = (item: Brand, focusDescription = false) => {
    setName(item.name)
    setNotes(item.details || '')
    setImages(item.images || [])
    setEditingId(item.id)
    setIsEditing(true)
    setIsAddDialogOpen(true)
    setShouldFocusDescription(focusDescription)
  }

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newName = e.target.value
    setName(newName)
    if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }))
  }

  const handleSave = () => {
    const newErrors: { name?: string } = {}
    if (!name.trim()) newErrors.name = 'Brand name is required'
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }
    const payload: {
      name: string
      details: string
      images: string[]
      id?: string
      catalogSlug?: string
      catalogBrandId?: number
    } = { name, details: notes, images }
    if (isEditing && editingId) {
      payload.id = editingId
    } else if (selectedPlatformBrand) {
      // Only on create: records which catalog brand this row came from, so
      // product lookups query the catalog slug rather than the name-derived one.
      payload.catalogSlug = selectedPlatformBrand.slug
      payload.catalogBrandId = selectedPlatformBrand.id
    }
    saveBrand.mutate(payload)
  }

  return (
    <div className="space-y-6">
      <PageBreadcrumb entityLabel={selectedBrand?.name} />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Brands</h2>
          <p className="text-muted-foreground">Manage the brands available in your store.</p>
        </div>
        {(brands.length > 0 || loading) && (
          <Button
            onClick={() => {
              setIsEditing(false)
              setIsAddDialogOpen(true)
            }}
            className="transition-all hover:scale-105 hover:shadow-md"
          >
            <Plus className="mr-0.5 h-4 w-4" /> Add Brand
          </Button>
        )}

        <FormDialog
          open={isAddDialogOpen}
          onOpenChange={handleCloseDialog}
          title={isEditing ? 'Edit Brand' : 'Add Brand'}
          description={
            isEditing
              ? 'Update existing brand details.'
              : "Create a new brand here. Click save when you're done."
          }
          onConfirm={handleSave}
          confirmLabel={
            isUploading
              ? 'Uploading...'
              : isEditing
                ? 'Update Brand'
                : 'Save Brand'
          }
          isPending={saveBrand.isPending}
          isConfirmDisabled={isUploading || !name.trim()}
          size="large"
          onOpenAutoFocus={(e) => {
            if (shouldFocusDescription) {
              e.preventDefault()
              descriptionRef.current?.focus()
            }
          }}
        >
          <div className="flex flex-col md:flex-row gap-6">
            <div className="flex-1 space-y-4">
              <div className="grid gap-2">
                <Label htmlFor="name" className="gap-0.5">
                  Brand Name<span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="name"
                    ref={nameInputRef}
                    placeholder="e.g. Pure Green"
                    required
                    value={name}
                    onChange={handleNameChange}
                    onFocus={() => {
                      if (platformBrands.length > 0 && !isEditing) {
                        setSuggestionsDismissed(false)
                      }
                    }}
                    className={
                      errors.name
                        ? 'border-destructive focus-visible:ring-destructive'
                        : ''
                    }
                    autoComplete="off"
                  />
                  {isSearchingPlatform && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    </div>
                  )}
                  {showSuggestions && platformBrands.length > 0 && !isEditing && (
                    <div
                      ref={suggestionsRef}
                      className="absolute z-50 w-full mt-1 bg-popover border rounded-md shadow-lg max-h-[200px] overflow-y-auto"
                    >
                      {platformBrands.map((brand) => (
                        <div
                          key={brand.id}
                          className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-muted transition-colors"
                          onClick={() => handleSelectPlatformBrand(brand)}
                        >
                          <div className="h-8 w-8 rounded-md border flex-shrink-0 overflow-hidden bg-muted/50">
                            {brand.logo ? (
                              <ImageWithSkeleton
                                src={brand.logo}
                                alt={brand.name}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                                <Tag className="h-4 w-4" />
                              </div>
                            )}
                          </div>
                          <span className="text-sm font-medium truncate">
                            {brand.name}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {errors.name && (
                  <p className="text-sm text-destructive">{errors.name}</p>
                )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="notes">Description</Label>
                <Textarea
                  id="notes"
                  ref={descriptionRef}
                  placeholder="Enter description about this brand..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="h-[180px] md:h-[220px] resize-none"
                />
              </div>
            </div>

            <div className="w-full md:w-auto flex flex-col gap-2">
              <Label>Brand Image</Label>
              <div className="flex items-start">
                <ImageUpload
                  value={images}
                  onChange={(url) => setImages([url])}
                  onRemove={() => setImages([])}
                  onUploadStart={() => setIsUploading(true)}
                  onUploadEnd={() => setIsUploading(false)}
                  variant="default"
                />
              </div>
            </div>
          </div>
        </FormDialog>

      </div>

      {(brands.length > 0 || searchQuery || loading) && (
        <div className="flex items-center gap-2 max-w-sm w-full">
          <div className="relative flex-1 group">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search brands..."
              className="pl-9 pr-8"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
            {searchInput && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSearchInput('')}
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:bg-muted hover:text-foreground transition-all"
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Clear search</span>
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            title="Refresh"
            disabled={isRefreshing}
            onClick={() => {
              setIsRefreshing(true)
              void brandsQuery.refetch()
              setTimeout(() => setIsRefreshing(false), 600)
            }}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      )}

      <div className="flex flex-col md:flex-row gap-6 items-start">
        {/* Brands Table */}
        <div
          className={`flex-1 min-w-0 transition-all duration-300 ${!loading && brands.length === 0 ? '' : 'border rounded-md squircle bg-card shadow-sm overflow-hidden'} ${selectedBrand ? 'w-full md:w-[calc(100%-340px)] lg:w-[calc(100%-380px)]' : 'w-full'}`}
        >
          <div className="">
            {!loading && brands.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-24 gap-6 text-center">
                <div className="relative w-24 h-24 opacity-10 grayscale shrink-0">
                  <img
                    src="/misc/logo.png"
                    alt="Rootpoint Logo"
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex flex-col gap-4 items-center">
                  <p className="font-medium text-muted-foreground text-lg">No brands found</p>
                  <Button
                    onClick={() => {
                      setIsEditing(false)
                      setIsAddDialogOpen(true)
                    }}
                    className="w-fit"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Brand
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                      <TableHead className="w-[300px]">Brand</TableHead>
                      <TableHead
                        className={`hidden md:table-cell ${selectedBrand ? 'md:hidden xl:table-cell' : ''}`}
                      >
                        Details
                      </TableHead>
                      <TableHead className="w-[110px] text-right">Products</TableHead>
                      <TableHead className="text-right w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading ? (
                      // Skeleton Loading
                      Array.from({ length: 5 }).map((_, i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Skeleton className="h-10 w-10 rounded-md" />
                              <div className="space-y-2">
                                <Skeleton className="h-4 w-[150px]" />
                                <Skeleton className="h-3 w-[100px]" />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell
                            className={`hidden md:table-cell ${selectedBrand ? 'md:hidden xl:table-cell' : ''}`}
                          >
                            <Skeleton className="h-4 w-[200px]" />
                          </TableCell>
                          <TableCell className="text-right">
                            <Skeleton className="h-4 w-8 ml-auto" />
                          </TableCell>
                          <TableCell className="text-right">
                            <Skeleton className="h-8 w-8 rounded-full ml-auto" />
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      brands.map((item) => (
                        <TableRow
                          key={item.id}
                          className={`cursor-pointer transition-colors group ${effectiveSelectedId === item.id || effectiveSelectedId === item.slug ? 'bg-muted' : ''}`}
                          onClick={() => {
                            if (item.slug === effectiveSelectedId || item.id === effectiveSelectedId) {
                              closeSelectedBrand()
                              return
                            }
                            setSelectedId(item.slug || item.id)
                          }}
                        >
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-3">
                              <div className="h-10 w-10 rounded-md flex items-center justify-center overflow-hidden bg-muted/50">
                                <ImageWithSkeleton
                                  src={
                                    item.images?.[0] ??
                                    (resolvedTheme === 'dark'
                                      ? '/placeholder-dark/Place%20Holder%20Dark%204_4.png'
                                      : '/placeholder-light/Place%20Holder%20Light%204_4.png')
                                  }
                                  alt={item.name}
                                  wrapperClassName="h-full w-full"
                                  className="h-full w-full object-cover"
                                />
                              </div>
                              <div>
                                {item.name}
                                {item.slug && (
                                  <span className="block text-xs text-muted-foreground">
                                    {item.slug}
                                  </span>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell
                            className={`hidden md:table-cell ${selectedBrand ? 'md:hidden xl:table-cell' : ''}`}
                          >
                            {item.details ? (
                              <div className="text-sm">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="cursor-help">
                                      {item.details.split(' ').length > 7
                                        ? item.details.split(' ').slice(0, 7).join(' ') + ' ...'
                                        : item.details}
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="max-w-xs break-words">{item.details}</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            ) : (
                              <div
                                className="cursor-pointer min-w-[110px]"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleEdit(item, true)
                                }}
                              >
                                <span className="text-muted-foreground group-hover:hidden whitespace-nowrap">
                                  —
                                </span>
                                <span className="text-muted-foreground hidden group-hover:inline text-sm hover:text-foreground transition-colors whitespace-nowrap">
                                  Add description
                                </span>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-sm">
                            {item._count?.products ?? 0}
                          </TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  className="h-8 w-8 p-0 opacity-100 xl:opacity-0 xl:group-hover:opacity-100 transition-opacity"
                                >
                                  <span className="sr-only">Open menu</span>
                                  <MoreHorizontal className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-32">
                                <DropdownMenuItem
                                  className="gap-1.5"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleEdit(item)
                                  }}
                                >
                                  <Pencil className="h-4 w-4" />Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="gap-1.5 text-destructive focus:text-destructive"
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setBrandToDelete(item)
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
                {/* Pagination Controls - Hide if no data */}
                {brands.length > 0 && (
                <DataTablePagination
                  page={page}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={limit}
                  entityName="brands"
                  isLoading={loading}
                  onPageChange={(p: number) => navigate({ to: '.', search: (prev) => ({ ...prev, page: p }), replace: true })}
                  onLimitChange={(l: number) => navigate({ to: '.', search: (prev) => ({ ...prev, limit: l, page: 1 }), replace: true })}
                />
                )}
              </>
            )}
          </div>
        </div>

        {/* Details Side Panel */}
        {selectedBrand && (
          <Card className="squircle mx-0.5 mb-0.5 w-full md:w-[320px] lg:w-[360px] sticky top-0 flex flex-col h-fit max-h-[calc(100vh-6.5rem)] overflow-hidden animate-in slide-in-from-right-10 fade-in duration-300 shadow-lg pb-4">
            <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2 bg-card z-10">
              <div className="grid gap-0">
                <CardTitle className="text-xl flex items-center gap-1.5 leading-tight">{selectedBrand.name}</CardTitle>
                <CardDescription className="font-mono text-xs -mt-0.5">
                  {selectedBrand.slug}
                </CardDescription>
              </div>
              <div className="flex items-center gap-1 -mt-1">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-muted-foreground"
                    >
                      <MoreVertical className="h-4 w-4" />
                      <span className="sr-only">Open menu</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-32">
                    <DropdownMenuItem
                      onClick={() => handleEdit(selectedBrand)}
                      className="gap-1.5 cursor-pointer"
                    >
                      <Pencil className="h-4 w-4" />Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setBrandToDelete(selectedBrand)}
                      className="gap-1.5 text-red-500 hover:text-red-600 focus:text-red-600 focus:bg-red-50 dark:focus:bg-red-950/30 cursor-pointer"
                    >
                      <Trash2 className="h-4 w-4" />Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeSelectedBrand}
                  className="transition-transform duration-200"
                >
                  <X className="h-4 w-4" />
                  <span className="sr-only">Close</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="grid gap-4 overflow-y-auto pb-0 px-6">
              <div className="flex flex-row gap-4 items-start">
                 <div className="w-24 h-24 rounded-lg bg-muted/50 flex items-center justify-center shrink-0 overflow-hidden relative">
                  <ImageWithSkeleton
                    src={
                      selectedBrand.images && selectedBrand.images.length > 0
                        ? selectedBrand.images[0]
                        : resolvedTheme === 'dark'
                          ? '/placeholder-dark/Place%20Holder%20Dark%204_4.png'
                          : '/placeholder-light/Place%20Holder%20Light%204_4.png'
                    }
                    alt={selectedBrand.name}
                    className={`w-full h-full object-cover${!(selectedBrand.images && selectedBrand.images.length > 0) ? ' opacity-50' : ''}`}
                  />
                 </div>
                <div className="space-y-1 flex-1">
                  <h4 className="font-medium leading-none">Description</h4>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {selectedBrand.details || (
                      <span
                        className="text-muted-foreground cursor-pointer hover:text-foreground transition-colors inline-block scale-x-150"
                        title="Add description"
                        onClick={() => handleEdit(selectedBrand, true)}
                      >
                        —
                      </span>
                    )}
                  </p>
                </div>
              </div>

              <div className="pt-2 mt-2 border-t border-border/50 text-xs text-muted-foreground">
                <span className="font-medium text-foreground block mb-1">Products</span>
                <span className="tabular-nums">{selectedBrand._count?.products ?? 0}</span>
              </div>

              <div className="pt-2 mt-2 border-t border-border/50 grid grid-cols-2 gap-4 text-xs text-muted-foreground">
                <div>
                  <span className="font-medium text-foreground block mb-1">Created</span>
                  {selectedBrand.createdAt
                    ? formatInStoreTz(selectedBrand.createdAt, 'MMM d, yyyy')
                    : 'N/A'}
                </div>
                <div>
                  <span className="font-medium text-foreground block mb-1">Updated</span>
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    {selectedBrand.updatedAt
                      ? formatDistanceToNow(new Date(selectedBrand.updatedAt), {
                          addSuffix: true,
                        })
                      : 'N/A'}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
      <DeleteConfirmationDialog
        open={!!brandToDelete}
        onOpenChange={(open: boolean) => !open && setBrandToDelete(null)}
        title="Delete Brand?"
        description={
          <>
            This action cannot be undone. This will permanently delete the brand{' '}
            <span className="font-medium text-foreground">"{brandToDelete?.name}"</span> and
            remove it from our servers.
          </>
        }
        onConfirm={() => {
          if (brandToDelete) {
            deleteBrand.mutate(brandToDelete.id)
            setBrandToDelete(null)
          }
        }}
        confirmLabel="Delete Brand"
        isPending={deleteBrand.isPending}
      />
    </div>
  )
}
